/**
 * The report builder.
 *
 * The configuration column shows what you have CHOSEN, not everything you
 * could choose. A dozen measures and fourteen dimensions do not fit above the
 * fold, and a panel you scroll past to reach the preview hides the answer
 * behind the question. Every list — measures, rows, columns, sort — is a shelf
 * with its own add button, so adding is always in the same place as the thing
 * being added to.
 *
 * Filters sit above the report rather than in that column: they are the
 * question's context ("for these customers, in this period") while the shelves
 * are its shape, and someone reading a finished report needs the context in
 * view. The PERIOD is one of those filters and rides on the same bar — see
 * `filter-bar.tsx`.
 *
 * Every control's enabled state comes from the shared guard functions, so a
 * greyed-out option and a server refusal can never disagree. A refusal that
 * LIFTS stays in the picker with its reason attached — "already on Columns",
 * "ten group-bys is the ceiling" — because a row that vanished as the draft
 * changed reads as a bug. A refusal that never lifts is not listed: a
 * dimension that can never be an axis is a column, and it is offered in the
 * pickers where it can actually be used.
 */
import {
  allowsKind,
  canGroup,
  canShowAs,
  canSort,
  canVisualize,
  columnAvailability,
  type DateRangeValue,
  DEFAULT_DETAIL_LIMIT,
  type DimValue,
  defaultSortDir,
  dimensionAvailability,
  findDimension,
  findMeasure,
  isAdditive,
  type Measure,
  modelPeriods,
  periodRefusal,
  planKey,
  type QueryPlan,
  REPORT_KIND_LABELS,
  type ReportKind,
  type ReportModel,
  type ReportSpec,
  SHOW_AS_MODES,
  type ShowAsMode,
  type SortSpec,
  showAsFor,
  specDateRange,
  type UIReportSpec,
  VIZ_KINDS,
  type VizKind,
  valueFormatFor,
  vizAvailability,
} from '@clockwise/semantic-core';
import {
  DATE_RANGE_PRESET_LABELS,
  FormulaHintProvider,
  Picker,
  prettyDateRange,
} from '@clockwise/ui';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  type ChangeEvent,
  type KeyboardEvent,
  type ReactNode,
  type Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FilterBar, type FilterControlContext } from '../filters/filter-bar';
import { filterFieldsOf } from '../filters/filter-fields';
import { type Transport, useReportQuery } from '../hooks/use-report-query';
import { type Axis, type SpecAction, useReportSpec } from '../hooks/use-report-spec';
// The heading sentence and the name suggested from it are ONE thing, and it
// lives in `../name` so a host can offer that suggestion without re-deriving
// the sentence the reader is looking at while they accept or reject it.
import { describeSpec, MAX_REPORT_DESCRIPTION, MAX_REPORT_NAME } from '../name';
import type { PopupAnchor } from '../render/anchored';
import type { OnDrill } from '../render/drill';
import { Pager } from '../render/pager';
import { ReportFooter, ReportView } from '../render/report-view';
import type { DragPayload } from './drag';
import { FormatMenu, type OnFormatColumn } from './format-menu';
import {
  activeFormatKeys,
  badgeFor,
  badgeIsNegative,
  FORMAT_OPTIONS,
  formatContext,
  ruleSample,
} from './format-options';
import { ModelPicker } from './model-picker';
import { Shelf, type ShelfRow } from './shelf';

export const SHOW_AS_LABELS: Record<ShowAsMode, string> = {
  raw: 'Default',
  pctRow: '% of row',
  pctCol: '% of column',
  pctTotal: '% of total',
};

const VIZ_LABELS: Record<VizKind, string> = {
  table: 'Table',
  bar: 'Bar',
  line: 'Line',
  area: 'Area',
  pie: 'Pie',
  // A number for one period, its movement against the period before, and its
  // history. Named for what it IS rather than "KPI", which says nothing about
  // how it behaves — see `metricTileSchema` for the contract.
  metric: 'Metric',
};

/**
 * The second question — where the model offers a choice about it.
 *
 * Asked explicitly rather than inferred from "did you put anything on the
 * column axis", because the vocabulary differs (a correlation id is a column
 * and not an axis), the controls differ (totals and % of row against paging and
 * a column order), and the kind is stored on a spec that gets saved and shared.
 *
 * The two words are `REPORT_KIND_LABELS`, which the guards also refuse a kind
 * in — a reader told "Utilization cannot be built as a Tabular report" pressed
 * a button that said Tabular. The hint below carries the plain-English version,
 * so those two words do not have to do the whole job.
 */
const KIND_HINTS: Record<ReportKind, string> = {
  pivot: 'Group the rows and total them — utilization by employee across months',
  detail: 'One row per entry, with the columns you choose',
};

/** Stable identity for a chain entry — a measure may appear once per column. */
const sortKey = (s: SortSpec) => `${s.by}|${(s.at ?? []).join('~')}`;

/**
 * What a level's DEFAULT order is called — the order the query sent.
 *
 * THE PHRASE EVERY OTHER TOOL USES. Tableau's sort dialog and Excel's pivot
 * options both call it "Data source order"; Qlik calls it "load order". A
 * reader who has sorted a pivot anywhere else has met this option under this
 * name, and matching it beats a word of our own that they would have to work
 * out from scratch.
 *
 * The order itself is the compiler's, not this label's: a pivot's `limit` is
 * unconditional, so the statement always carries the `ORDER BY` over the
 * group-by tuple that a cap needs to be reproducible, and `orderTuples` keeps
 * that order for every level with no entry of its own. Key ascending, every
 * time — which on a time dimension is the calendar, because every one of them
 * keys on text that sorts chronologically (`YYYY-MM`, `YYYY-Www`, an ISO
 * date), a convention the model keeps deliberately; see `month` and `week`.
 *
 * THAT is the thing the name has to get across, and it is why this is not "As
 * returned", which is what it said first. "As returned" described how the rows
 * ARRIVED rather than what decides their order, so the date order — the one
 * order somebody with months across the top usually wants — read as the
 * absence of an order, and the entry beneath it, the field's own label, looked
 * like the way to ask for it. It is not: where a label is not its key that is
 * the alphabetical order, `Aug 2026` before `Feb 2026`.
 *
 * NO TOOLTIP SPELLING THAT OUT, though it is tempting. A `title` on the option
 * never renders where it would be read — macOS draws the popup itself — and it
 * could not tell the truth in any case: a level with no entry of its own falls
 * back to a LEGACY WHOLE-AXIS CHAIN where a saved report carries one, so "by
 * this field's key, ascending" would be a specific, checkable, wrong claim on
 * exactly the reports whose order a reader cannot otherwise explain. The label
 * stays vague because the thing it names is not always the same thing; see
 * `levelSort`, which calls that under-reporting the safe side of the trade.
 */
const KEY_ORDER_LABEL = 'Data source order';

/**
 * Fold a picker's new selection back into an ORDERED shelf.
 *
 * The picker hands back a set, in its own list order; the shelf's order is the
 * report's — the reader dragged those columns into that sequence. Taking the
 * picker's order wholesale would silently reshuffle a hand-arranged listing
 * every time a box was ticked, so the existing entries keep their positions and
 * only genuinely new ids are appended.
 */
function reconcile(current: readonly string[], next: readonly string[]): string[] {
  const chosen = new Set(next);
  const currently = new Set(current);
  return [...current.filter((id) => chosen.has(id)), ...next.filter((id) => !currently.has(id))];
}

export interface ReportBuilderProps {
  /**
   * The models this builder can report on. With more than one, a picker
   * appears — utilization and budgets answer different questions and share no
   * vocabulary, so choosing between them is the first decision, not a setting.
   */
  models: readonly ReportModel[];
  transport: Transport;
  /**
   * A reason to re-run the CURRENT plan, when the plan itself did not change.
   *
   * `planKey` — what decides whether to refetch — covers columns, filters,
   * measures, sort and period: everything the SERVER answers with different
   * rows for. It says nothing about a question like "and send back the SQL
   * you compiled", which changes what `transport` asks for without changing
   * any of that. Pass whatever the host's `transport` closes over that can
   * vary this way — `includeSql`, say — and toggling it re-asks even though
   * the plan looks identical. See {@link useReportQuery}.
   */
  queryDependsOn?: string | number | boolean;
  initialSpec?: UIReportSpec;
  /**
   * The report's name, as saved. Empty means unnamed, and the heading falls
   * back to describing the draft — see {@link ReportTitle}.
   *
   * `initial`, like `initialSpec`: the builder owns the edit from here, so a
   * host that re-renders with a stale prop cannot yank a half-typed name out
   * from under the caret. A host that needs the current value reads it from
   * `onDraftChange`.
   */
  initialName?: string;
  /** Fires when the name is committed — on Enter or on blur, not per keystroke. */
  onNameChange?: (name: string) => void;
  /**
   * The report's description, as saved. Empty means it has none.
   *
   * `initial` for the same reason as {@link initialName}: the builder owns the
   * edit from here, and the current value comes back through `onDraftChange`.
   */
  initialDescription?: string;
  /** Fires when the description is committed — on Enter or on blur, as the name does. */
  onDescriptionChange?: (description: string) => void;
  /**
   * May the reader edit the name and the description in place? Default yes.
   *
   * PASS FALSE WHEN THE HOST HAS NOWHERE TO PUT THE RESULT. The builder has no
   * Save button of its own, so an in-place edit is only ever a proposal the
   * host persists — and a host that cannot save (no `report.edit`, a report
   * shared read-only) would otherwise leave a line that invites a description,
   * accepts one, renders it exactly as a saved one, and drops it on the next
   * navigation with nothing said. The lines stay READABLE either way; they
   * simply stop being controls.
   */
  editable?: boolean;
  /**
   * Draw the report's name and description above the preview? Default yes.
   *
   * PASS FALSE WHEN THE HOST ALREADY SHOWS THEM. A host with a page header of
   * its own — a title, a description, the actions that act on the report — was
   * printing both twice: once in its header and once again on top of the table,
   * with the second copy the only editable one. The builder still OWNS the two
   * values either way; a host that hides the block edits them through
   * {@link ReportBuilderHandle.setDetails} and reads them back off the draft.
   */
  showTitle?: boolean;
  /**
   * May the reader change the report's SHAPE? Default yes.
   *
   * PASS FALSE FOR A READER WHO MAY RUN A REPORT BUT NOT BUILD ONE — someone
   * a saved report was shared with, holding `report.view` alone. The whole
   * configuration column is then not drawn: the model, the kind, the shelves
   * and the display options are gone, and what is left is the filter bar, the
   * result and its footer. THE CONTROLS THAT SURVIVE ARE THE ONES THAT ASK THE
   * QUESTION AGAIN RATHER THAN CHANGE IT — filters, the period, a column sort,
   * a page — all of which that reader is entitled to (CR-1.5), none of which
   * they could save.
   *
   * Hiding the column and not merely disabling it: a shelf of greyed-out
   * chips is a screen mostly given over to telling somebody what they may not
   * do, and this reader did not come here to build anything. It is also NOT a
   * security boundary — the server decides what may be saved, and nothing here
   * is load-bearing for that. It is an offer withdrawn, not a lock.
   *
   * `editable` is the neighbouring question and a narrower one: whether the
   * NAME and the DESCRIPTION may be typed over. A host may well pass false to
   * both, but they are two capabilities and the reasons differ.
   */
  configurable?: boolean;
  /**
   * The current draft, whenever it changes — for what the host RENDERS from it.
   *
   * Enabling a Save button, showing a dirty marker. NOT for what a click acts
   * on: see {@link ReportBuilderHandle.draft}, which is exact at the instant it
   * is called, where this is at best a render behind.
   *
   * The builder renders no Save button of its own because saving is not a
   * property of a report builder — it needs an API, a permission and somewhere
   * to navigate afterwards, none of which belong in here — and a library button
   * would fix its position on the page as well.
   */
  onDraftChange?: (draft: ReportDraft) => void;
  /**
   * Ref handle for reading the draft at the moment of an action.
   *
   * React 19 takes `ref` as an ordinary prop, so this is `handle` rather than
   * `ref` only to say plainly that it is not a DOM node.
   */
  handle?: Ref<ReportBuilderHandle>;
  /** Option lists per dimension id, for the filter bar. */
  filterOptions?: Record<string, DimValue[]>;
  /**
   * Supply the host's own value control for a filter — return null to keep the
   * built-in one. The seam that lets an app put its real entity pickers on the
   * bar instead of a second, near-identical autocomplete living in here.
   */
  renderFilterControl?: (context: FilterControlContext) => ReactNode;
  /**
   * A figure in the result table was clicked. OMIT and the table's cells are
   * plain text. The builder decides nothing about where a drill goes — that
   * needs the host's saved reports and its API — it only reports the click.
   */
  onDrill?: OnDrill;
  /**
   * The host's drill-down, docked under the result — where the SQL disclosure
   * sits, and sized the same way (see `.rk-drilltray`). Drill state is the
   * HOST's and never touches the draft: a drill is reading the report, not
   * editing it, and must not arm an unsaved-changes prompt.
   */
  drillTray?: ReactNode;
}

/** What the builder currently holds — see {@link ReportBuilderProps.onDraftChange}. */
export interface ReportDraft {
  /**
   * The runnable spec, or `null` while the draft is still missing a measure or
   * a column. `null` is what a host disables its Save button on: there is no
   * report yet to save, and the builder says so rather than leaving the host to
   * re-derive the rule.
   */
  spec: ReportSpec | null;
  /** Trimmed. Empty means the report has not been named. */
  name: string;
  /** Trimmed. Empty means the report has no description — NOT one that is `""`. */
  description: string;
}

/** What {@link ReportBuilderProps.handle} exposes. */
export interface ReportBuilderHandle {
  /**
   * The draft RIGHT NOW — call it inside the click that acts on it.
   *
   * Exact by construction: it reads refs rather than state that has been
   * pushed out and may still be in flight. Clicking a host's Save button while
   * the rename field has focus commits the name and saves in one gesture, and
   * only a value read at click time can be sure to include it.
   */
  draft(): ReportDraft;
  /**
   * Set the name and the description from OUTSIDE the builder.
   *
   * The counterpart to {@link ReportBuilderProps.showTitle}: a host that draws
   * the title block itself needs somewhere to put what its own editor
   * collected, and the builder is still the one that owns those two values —
   * `draft()` reads them, and a host that kept its own copy would be the second
   * answer to "what is this report called". Both are trimmed on the way in, and
   * an empty string means UNSET, exactly as an in-place edit that clears the
   * line does. Fires `onNameChange`/`onDescriptionChange` like any other commit.
   */
  setDetails(details: { name: string; description: string }): void;
}

export function ReportBuilder({ models, initialSpec, ...rest }: ReportBuilderProps) {
  const [modelId, setModelId] = useState(initialSpec?.model ?? models[0]?.id);
  const model = models.find((m) => m.id === modelId) ?? models[0];
  if (!model) throw new Error('ReportBuilder needs at least one model');

  // One provider for every ƒ hint in the builder — picker rows, shelves and
  // the preview's headers — so moving between them skips the hover delay.
  return (
    <FormulaHintProvider>
      <Builder
        // Remounting on a model change is the point: a spec written against
        // one model's dimensions means nothing against another's, and carrying
        // it over would leave a report naming columns that do not exist.
        key={model.id}
        model={model}
        models={models}
        onModel={setModelId}
        initialSpec={initialSpec?.model === model.id ? initialSpec : undefined}
        {...rest}
      />
    </FormulaHintProvider>
  );
}

function Builder({
  model,
  models,
  onModel,
  transport,
  queryDependsOn,
  initialSpec,
  initialName,
  onNameChange,
  initialDescription,
  onDescriptionChange,
  editable = true,
  showTitle = true,
  configurable = true,
  onDraftChange,
  handle,
  filterOptions = {},
  renderFilterControl,
  onDrill,
  drillTray,
}: Omit<ReportBuilderProps, 'models'> & {
  model: ReportModel;
  models: readonly ReportModel[];
  onModel: (id: string) => void;
}) {
  const { spec, dispatch, runnable, plan, setSortBy } = useReportSpec(model, initialSpec);
  const filterFields = useMemo(() => filterFieldsOf(model), [model]);
  const { data, loading, error } = useReportQuery(runnable, plan, transport, queryDependsOn);
  const [name, setName] = useState(initialName ?? '');
  const [description, setDescription] = useState(initialDescription ?? '');

  /**
   * Is the configuration column on screen?
   *
   * OPEN ON ARRIVAL, and only ever closed by a click. Somebody who may build a
   * report is here to build it, and a panel that had to be found before the
   * first shelf could be reached would put a step in front of the common
   * errand to spare the rare one.
   *
   * Session state, deliberately not persisted and not in the spec: it is where
   * this reader has the panel RIGHT NOW, not a property of the report, and a
   * remembered collapse would greet the next visit with a builder that appears
   * to have no controls. Held here rather than in `useReportSpec` for the same
   * reason — nothing about it belongs in what gets saved.
   *
   * Ignored entirely when {@link ReportBuilderProps.configurable} is false:
   * there is no column to collapse and no toggle offering one.
   */
  const [configOpen, setConfigOpen] = useState(true);
  const showConfig = configurable && configOpen;
  /**
   * Which measure's Format menu is open, where, and what gets focus back when
   * it closes. Local: not part of the report.
   *
   * ONE MENU for the whole builder, whether it was opened from a column header
   * or from the Values shelf — two entrances to the same place, so they can
   * never disagree about what a column's format is.
   */
  const [formatTarget, setFormatTarget] = useState<{
    measureId: string;
    anchor: PopupAnchor;
    returnFocus: HTMLElement | null;
  } | null>(null);
  const formatToggleRef = useRef<HTMLElement | null>(null);
  const openFormat: OnFormatColumn = (measureId, anchor, returnFocus) => {
    // Pressing the same trigger again CLOSES, as any menu button does.
    if (formatTarget?.measureId === measureId && formatTarget.returnFocus === returnFocus) {
      setFormatTarget(null);
      return;
    }
    formatToggleRef.current = returnFocus;
    setFormatTarget({ measureId, anchor, returnFocus });
  };
  const closeFormat = () => {
    const back = formatTarget?.returnFocus;
    setFormatTarget(null);
    // The menu is unmounting; hand focus back after it has gone.
    // Only when focus went down with the menu (Escape, ✕). A click OUTSIDE
    // also closes it, and by the next frame that click has focused whatever it
    // landed on — a title field, a filter search — which must keep it.
    requestAnimationFrame(() => {
      const active = document.activeElement;
      if (back?.isConnected && (!active || active === document.body)) back.focus();
    });
  };
  const formatMeasure = formatTarget ? findMeasure(model, formatTarget.measureId) : undefined;

  const [drag, setDrag] = useState<DragPayload | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);
  const [activeZone, setActiveZone] = useState<string | null>(null);

  const dimAvailability = useMemo(() => dimensionAvailability(model, spec), [model, spec]);
  const vizOptions = useMemo(() => vizAvailability(model, spec), [model, spec]);

  const label = (id: string) => findDimension(model, id)?.label ?? id;
  // Dimensions too, not just measures: a tabular report sorts by its own
  // COLUMNS, so a chip that resolved only measures showed the raw field id.
  const sortLabel = (by: string) =>
    by === 'label'
      ? 'Name'
      : (findMeasure(model, by)?.label ?? findDimension(model, by)?.label ?? by);

  // EVERY picker in this builder is the same multi-select panel: tick what
  // belongs on the shelf, untick what does not, and the whole vocabulary stays
  // visible with the chosen ones marked. One control to learn, and the panel
  // answers "what is on this report" as well as "what could be" — which the
  // old filter-out-the-taken behaviour could not.
  const measureOptions = model.measures.map((m) => ({
    id: m.id,
    label: m.label,
    description: m.description,
    formula: m.formulaText,
    group: m.group,
  }));

  /**
   * The group-by vocabulary for ONE axis.
   *
   * A dimension that can NEVER be an axis is not listed at all. Keka ID, QB ID
   * and a free-text description are permanent refusals — no amount of
   * rearranging makes them groupable — and half a picker greyed out is a
   * picker you read twice to find the four rows you can use. They remain
   * offered where they DO belong: the Tabular column picker and the filter bar.
   *
   * The refusals that stay are the ones that LIFT. "Already on Columns" and
   * "ten group-bys is the ceiling" both describe the draft rather than the
   * dimension, so the row has to be there to explain where the thing went and
   * what to remove — a dimension that vanished when the last slot was spent
   * would read as a bug.
   */
  const dimensionOptions = (axis: Axis) => {
    const other = axis === 'rows' ? spec.cols : spec.rows;
    return model.dimensions
      .filter((d) => canGroup(d) || other.includes(d.id))
      .map((d) => {
        const verdict = dimAvailability.get(d.id);
        return {
          id: d.id,
          label: d.label,
          description: d.description,
          group: d.group,
          disabledReason: other.includes(d.id)
            ? `${d.label} is already on ${axis === 'rows' ? 'Columns' : 'Rows'}.`
            : verdict && !verdict.ok
              ? verdict.reason
              : undefined,
        };
      });
  };

  /**
   * What a tabular report may put in a column: the WHOLE vocabulary.
   *
   * Dimensions and measures from ONE list, because a tabular report's fields
   * are one list — `Employee, Customer, Hours, Status` — and splitting the
   * picker in two would ask the reader to know which side of the model a field
   * lives on. `groupable: false` is not consulted; see `columnAvailability`.
   *
   * Unlike the pivot's pickers this does NOT drop what is already chosen: the
   * panel is multi-select, so a chosen field shows ticked and clicking it again
   * removes it. Filtering them out would make the ticks meaningless.
   */
  const columnOptions = () => {
    const available = columnAvailability(model);
    const refusal = (id: string) => {
      const verdict = available.get(id);
      return verdict && !verdict.ok ? verdict.reason : undefined;
    };
    return [
      ...model.dimensions.map((d) => ({
        id: d.id,
        label: d.label,
        description: d.description,
        group: d.group,
        disabledReason: refusal(d.id),
      })),
      ...model.measures.map((m) => ({
        id: m.id,
        label: m.label,
        description: m.description,
        formula: m.formulaText,
        group: m.group,
        // Kept in the list with its reason, like a refused dimension: "why
        // can't I list Overall Utilization?" is answered by seeing the
        // refusal, not by the row being absent.
        disabledReason: refusal(m.id),
      })),
    ];
  };

  /**
   * A tabular report sorts by its OWN columns — there is no group to be the
   * label of, and ordering by something the query does not select is not a
   * legal `ORDER BY`.
   */
  const columnSortOptions = () =>
    spec.columns.map((id) => {
      const dim = findDimension(model, id);
      if (dim) {
        return {
          id,
          label: dim.label,
          description: dim.description,
          disabledReason: canSort(dim) ? undefined : `${dim.label} cannot be sorted by.`,
        };
      }
      const measure = findMeasure(model, id);
      return { id, label: measure?.label ?? id, formula: measure?.formulaText };
    });

  const chainOf = (axis: Axis) => (axis === 'rows' ? spec.rowSort : spec.colSort);
  const setChain = (axis: Axis, sort: SortSpec[]) => dispatch({ type: 'setSort', axis, sort });

  const sortRows = (axis: Axis): ShelfRow[] =>
    chainOf(axis).map((sort, i) => ({
      id: sortKey(sort),
      label: sortLabel(sort.by),
      // A measure can be both a value and a sort key, and two buttons called
      // "Remove Units" doing different things is a coin toss.
      removeLabel: `Remove ${sortLabel(sort.by)} from the ${axis === 'rows' ? 'row' : 'column'} order`,
      // The direction is on the widget beside it, so the detail line carries
      // only what the widget cannot: which column this entry ranks by.
      detail: sort.at?.length ? `within ${sort.at.join(' › ')}` : undefined,
      controls: (
        <button
          type="button"
          className="rk-toggle rk-toggle-sm"
          // The direction lives on the chip because it belongs to THAT entry —
          // "region ascending, then revenue descending" needs one each.
          aria-label={`${sortLabel(sort.by)}: sort ${sort.dir === 'asc' ? 'descending' : 'ascending'}`}
          title={sort.dir === 'asc' ? 'Ascending' : 'Descending'}
          onClick={() =>
            setChain(
              axis,
              chainOf(axis).map((s, n) =>
                n === i ? { ...s, dir: s.dir === 'asc' ? 'desc' : 'asc' } : s,
              ),
            )
          }
        >
          {sort.dir === 'asc' ? '↑ Ascending' : '↓ Descending'}
        </button>
      ),
    }));

  /**
   * ONE PIVOT FIELD'S ORDER, drawn on the field.
   *
   * Sort by, direction, and — for a row level with another beneath it — the
   * two display flags. Open, not behind a click: the question a reader asks of
   * a pivot's shelf is "why are these in this order", and an answer that takes
   * four popovers to assemble is one nobody assembles.
   *
   * A level with no entry reads {@link KEY_ORDER_LABEL} and is exactly that:
   * the order the query sent, by the dimension's key, ascending. It is the
   * default because it is what a pivot has always done, and because the
   * alternative default — alphabetical by label — is wrong for the dimensions
   * most worth defaulting well, the dates: `Aug 2026` sorts before `Feb 2026`
   * by name and after it by key. Chosen deliberately it is fine; imposed it
   * would silently misorder every report with a month on an axis.
   *
   * ITS DIRECTION IS LIVE, and that is the point of it on a date: either
   * Descending writes a `by: 'key'` entry, the calendar newest-first — a trend
   * report that opens on this month rather than a year ago. Ascending under
   * data source order REMOVES the entry instead: it is the default, so the way
   * back to it is the absence of one, and a click on the already-pressed button
   * changes nothing. The select reads "Data source order" for an entry-less
   * level and for a key entry alike; only the Order pair says which way round.
   */
  /**
   * A pinned column tuple, in the words its header uses.
   *
   * `at` holds KEYS, because that is what a ranking has to match on — and a
   * key is `period-1` where the header says `2026-05`. Printed raw it named a
   * column the reader cannot find, which is worse than not saying which column
   * at all. Resolved off the result that is on screen, so the two agree by
   * construction; an unresolvable key falls back to itself rather than
   * vanishing, since a pin nobody can read is still a pin that is in force.
   */
  const pinnedAs = (at: readonly string[]) =>
    at
      .map((key, level) => {
        const dimensionId = spec.cols[level];
        if (!dimensionId) return key;
        for (const row of data?.rows ?? []) {
          const value = row.dims[dimensionId];
          if (value?.key === key && value.label) return value.label;
        }
        return key;
      })
      .join(' › ');

  const fieldSettings = (axis: Axis, dimensionId: string, index: number): ReactNode => {
    const dims = axis === 'rows' ? spec.rows : spec.cols;
    const dim = findDimension(model, dimensionId);
    const sort = chainOf(axis).find((s) => s.on === dimensionId);
    const name = dim?.label ?? dimensionId;
    // Ranking THIS level by a measure means adding that measure up over
    // everything beneath it — the inner levels, and the columns if there are
    // any. The same test the planner applies, so a greyed-out option and a
    // dropped sort entry can never disagree; see `aggregatesToRank` in
    // `plan.ts`. A column level always totals down the rows, and the planner
    // has never refused that.
    const aggregates = axis === 'cols' || spec.cols.length > 0 || index < dims.length - 1;
    const set = (by: string | null, dir?: 'asc' | 'desc') =>
      dispatch({ type: 'setFieldSort', axis, dimensionId, by, dir });
    // "Data source order" is the absence of an entry OR a `key` entry. See
    // the note above `fieldSettings`.
    const byKey = !sort || sort.by === 'key';
    // A level with no entry of its own on an axis that still carries a legacy
    // whole-axis chain is ordered BY THAT CHAIN, not by its key. A direction
    // written here would be a level entry, which `orderTuples` prefers over the
    // chain — so a click meant to reverse the order would replace it instead.
    const inherited = !sort && chainOf(axis).some((s) => s.on === undefined);
    const direction = sort?.dir ?? 'asc';
    const setDirection = (dir: 'asc' | 'desc') => {
      // Ascending by key IS the default, so it is the absence of an entry:
      // removing one is the way back to the order the query sent.
      if (byKey && dir === 'asc') {
        if (sort) set(null);
        return;
      }
      set(sort?.by ?? 'key', dir);
    };

    return (
      <>
        <label className="rk-fieldrow">
          <span className="rk-fieldlabel">Sort by</span>
          <select
            aria-label={`Sort ${name} by`}
            value={byKey ? '' : (sort?.by ?? '')}
            onChange={(e) => set(e.target.value === '' ? null : e.target.value)}
          >
            <option value="">{KEY_ORDER_LABEL}</option>
            {/* The dimension's OWN NAME, not the word "Name". At a level the
                label being sorted is that level's label, and "Customer" is
                what the reader is choosing between; "Name" was only ever
                unambiguous because the old chain had one of it per axis. */}
            <option value="label" disabled={dim ? !canSort(dim) : false}>
              {name}
            </option>
            {spec.measures.map((measureId) => {
              const measure = findMeasure(model, measureId);
              const refused = aggregates && measure && !isAdditive(measure);
              return (
                <option key={measureId} value={measureId} disabled={refused}>
                  {measure?.label ?? measureId}
                </option>
              );
            })}
          </select>
        </label>

        <div className="rk-fieldrow">
          <span className="rk-fieldlabel">Order</span>
          {/* Live: with nothing chosen the level is in data source order,
              ascending, and Descending reverses it. Disabled only while a
              legacy whole-axis chain orders the level — see `inherited`.

              No group role over the pair. Each button already announces the
              field AND the direction — "Customer: descending" — so a wrapper
              naming it a third time is noise, and the two `aria-pressed`
              states say between them which one is in force. */}
          <div className="rk-segmented">
            {(['asc', 'desc'] as const).map((dir) => (
              <button
                type="button"
                key={dir}
                aria-pressed={!inherited && direction === dir}
                disabled={inherited}
                aria-label={`${name}: ${dir === 'asc' ? 'ascending' : 'descending'}`}
                onClick={() => setDirection(dir)}
              >
                {dir === 'asc' ? 'Ascending' : 'Descending'}
              </button>
            ))}
          </div>
        </div>

        {/* Where the ranking was pinned to one column by clicking its header.
            The picker cannot say which March it means, so the pin is only ever
            SET from the header — but it has to be READABLE here, or a report
            ordered by one month looks like a report ordered by the year. */}
        {sort?.at?.length ? <p className="rk-fieldnote">Within {pinnedAs(sort.at)}</p> : null}

        {/* Both flags only mean something for a level that has another BELOW
            it. A subtotal of the innermost level is one row printed twice, and
            its values never repeat, because row tuples are distinct. */}
        {axis === 'rows' && index < dims.length - 1 ? (
          <div className="rk-fieldrow">
            <span className="rk-fieldlabel">Show</span>
            <div className="rk-fieldtoggles">
              <button
                type="button"
                className="rk-toggle rk-toggle-sm"
                aria-pressed={spec.subtotals.includes(dimensionId)}
                aria-label={`Show a total for each ${name}`}
                title="A total row beneath each group at this level"
                onClick={() => dispatch({ type: 'toggleSubtotal', dimensionId })}
              >
                {/* A SUBTOTAL, which is what the spec calls it and what it is.
                    Labelled "Totals" it collided with the whole-report switch
                    in Display, and the one people went looking for was the
                    other one. */}
                Subtotal
              </button>
              <button
                type="button"
                className="rk-toggle rk-toggle-sm"
                aria-pressed={spec.suppressRepeats.includes(dimensionId)}
                aria-label={`Hide repeated ${name} values`}
                title="Blank this column when it repeats the row above"
                onClick={() => dispatch({ type: 'toggleSuppressRepeats', dimensionId })}
              >
                Hide repeats
              </button>
            </div>
          </div>
        ) : null}
      </>
    );
  };

  /**
   * The sort chain, as a picker sees it: a set of FIELDS.
   *
   * `sortKey()` distinguishes chain entries because one measure can appear once
   * per column tuple, but a tick names the field, so ticking one off removes
   * every entry for it. That is the right trade for a checkbox: pinning a rank
   * to a single column is done by clicking that column's header, which is where
   * the reader can see which column they mean.
   *
   * Entries that survive keep their position AND their direction — re-deriving
   * the chain from the picker's order would reset a hand-built "region asc,
   * then revenue desc" to defaults on every tick.
   */
  const sortValue = (axis: Axis) => [...new Set(chainOf(axis).map((s) => s.by))];

  const applySort = (axis: Axis, next: string[]) => {
    const chain = chainOf(axis);
    const chosen = new Set(next);
    setChain(axis, [
      ...chain.filter((s) => chosen.has(s.by)),
      ...next
        .filter((by) => !chain.some((s) => s.by === by))
        .map((by): SortSpec => ({ by, dir: defaultSortDir(model, by) })),
    ]);
  };

  const detail = spec.kind === 'detail';
  /**
   * The kind a SAVED report named, when this model no longer offers it.
   *
   * `settle` coerces such a spec to a kind the model does offer — otherwise it
   * is a draft that cannot run and, with no kind control drawn, nothing on
   * screen can fix. But the coercion is silent: the shelves open empty, and a
   * reader without `report.edit` sees a report they saved reduced to a blank
   * preview with no account of why. Before models could refuse a kind, the
   * server's own refusal reached them through `rk-error`; this is what says it
   * instead, and it says it where the choice would have been.
   *
   * Read off the spec the HOST handed in rather than tracked as state: that
   * spec is the reason, and it does not change while the draft is edited.
   */
  const savedAsKind =
    initialSpec && !allowsKind(model, initialSpec.kind) ? initialSpec.kind : undefined;
  /**
   * The chart a SAVED report named, when its own shape no longer supports it.
   *
   * `settleViz` rewrites such a spec to a table — a saved bar whose third row
   * dimension arrived later, a line whose time dimension was since retired and
   * `pruneUnknown` dropped. The Display row used to carry that news by itself:
   * the refused chart sat there struck through with the guard's sentence on it.
   * Now the row lists only what the report CAN be drawn as, so without this the
   * coercion has no trace on screen at all — and `draft()` reads the settled
   * spec, so the next save, even one triggered by nothing but a rename, writes
   * `table` back and the saved chart is gone for good.
   *
   * Read off the HOST's spec for the same reason `savedAsKind` is: that spec is
   * the reason, and it must not change as the draft is edited. Comparing
   * `initialSpec.viz` against the live one instead would call the reader's own
   * switch to a line a coercion.
   *
   * Silent where the KIND was coerced too: those shelves open empty, so every
   * chart is refused and the notice above already says what happened.
   */
  const savedAsViz = (() => {
    if (!initialSpec || savedAsKind || initialSpec.kind === 'detail') return undefined;
    const verdict = canVisualize(model, initialSpec, initialSpec.viz);
    return verdict.ok ? undefined : { viz: initialSpec.viz, reason: verdict.reason };
  })();
  /**
   * The period a SAVED report was asked over, in words, when this model does
   * not offer it — a custom range on Budget vs Actuals.
   *
   * `settle` moves such a spec onto the model's first period, for the reason
   * `savedAsKind` gives: left alone it runs over a window the reader can see
   * named on the chip and has no control to reach. Said here because the move
   * changes the numbers, and `draft()` saves the moved period back.
   */
  const savedOverPeriod = (() => {
    if (!initialSpec || !periodRefusal(model, initialSpec.period ?? 'custom')) return undefined;
    const preset = initialSpec.period ?? 'custom';
    const name = DATE_RANGE_PRESET_LABELS[preset].toLowerCase();
    const dates = prettyDateRange({ from: initialSpec.from, to: initialSpec.to });
    return `${preset === 'custom' ? `a ${name}` : name} (${dates})`;
  })();
  const periodPresets = useMemo(() => modelPeriods(model), [model]);
  const refusePeriod = useCallback(
    (range: DateRangeValue) => periodRefusal(model, range.preset),
    [model],
  );
  // The kinds THIS model offers, in the builder's own display order rather
  // than the model's — see the kind control below for why Tabular leads.
  const kinds = (['detail', 'pivot'] as const).filter((k) => allowsKind(model, k));
  const offset = spec.offset ?? 0;
  const pageSize = spec.limit ?? DEFAULT_DETAIL_LIMIT;
  // The count survives the fetch that replaces it, so the pager can keep saying
  // "of 12,345" while the NEXT PAGE of the same report is in flight instead of
  // blanking. Written during render like `useReportQuery`'s own latest-spec
  // ref: it is a carbon copy of a value this render already has, not a decision.
  //
  // It does not survive a different QUESTION. Narrowing a filter from 12,345
  // rows to 40 would otherwise render "Showing 1–500 of 12,345 · Page 1 of 25"
  // for the report that is about to have one page — a count of a question
  // nobody is asking any more, and the number the reader is most likely to be
  // watching, since narrowing is why they touched the filter.
  const lastTotal = useRef<{ key: string; total: number } | undefined>(undefined);
  // The QUESTION, which is the query with the page taken out of it: `planKey`
  // counts `offset`, so keying on it directly would discard the count on every
  // page turn — the one case the memory exists for.
  const queryKey = plan ? planKey({ ...plan, sql: { ...plan.sql, offset: undefined } }) : '';
  if (data?.meta.totalRows !== undefined) {
    lastTotal.current = { key: queryKey, total: data.meta.totalRows };
  }
  const rememberedTotal = lastTotal.current?.key === queryKey ? lastTotal.current.total : undefined;

  // What the DRAFT is, in words. It is the heading of an unnamed report and the
  // sub-line of a named one — never dropped, because "Q3 board pack" does not
  // say which measures are on it and the sentence does.
  const derived = runnable
    ? describeSpec(model, runnable)
    : detail
      ? 'Pick a column'
      : 'Pick a measure and a row dimension';

  /**
   * THE DRAFT AS IT STANDS RIGHT NOW, in refs rather than in the host's state.
   *
   * `handle.draft()` is what a host's Save button reads, and it has to be exact
   * at the instant of the click. Pushing the draft out through a callback
   * cannot be: clicking Save while the rename field is focused is one gesture
   * and three events — the mousedown blurs the field, the blur commits the
   * name, the click saves — and any push is a render behind at least one of
   * them. The symptom is a rename that silently does nothing, which is what
   * this replaced.
   *
   * Refs sidestep the question entirely. `runnableRef` is written during
   * render, so it is current by definition; `nameRef` is written in
   * `commitName` as well, so it is right even before the render that commit
   * triggers has happened.
   */
  const nameRef = useRef(name);
  nameRef.current = name;
  const descriptionRef = useRef(description);
  descriptionRef.current = description;
  const runnableRef = useRef(runnable);
  runnableRef.current = runnable;
  const commitName = (next: string) => {
    if (next === name) return;
    nameRef.current = next;
    setName(next);
    onNameChange?.(next);
  };

  // Same shape as `commitName`, and the ref is written for the same reason: a
  // click on the host's Save button blurs this field and saves in one gesture,
  // so the description has to be readable before the render that commit causes.
  const commitDescription = (next: string) => {
    if (next === description) return;
    descriptionRef.current = next;
    setDescription(next);
    onDescriptionChange?.(next);
  };

  // MIRRORED IN A REF for the same reason everything else on the handle is: the
  // handle is built once, and a commit captured from the first render would
  // compare its `next` against the name this report had at mount — so clearing
  // a name and typing it back would be dropped as "no change".
  const commit = useRef({ name: commitName, description: commitDescription });
  commit.current = { name: commitName, description: commitDescription };
  useImperativeHandle(
    handle,
    () => ({
      draft: () => ({
        spec: runnableRef.current,
        name: nameRef.current.trim(),
        description: descriptionRef.current.trim(),
      }),
      setDetails: ({ name: nextName, description: nextDescription }) => {
        commit.current.name(nextName.trim());
        commit.current.description(nextDescription.trim());
      },
    }),
    [],
  );

  // The same draft, PUSHED — for whatever the host renders from it: an enabled
  // Save button, a dirty marker. A passive effect is right for that: a button
  // that enables a frame late is invisible, and the value a click acts on comes
  // from `handle.draft()` rather than from here.
  const draftListener = useRef(onDraftChange);
  draftListener.current = onDraftChange;
  const trimmedName = name.trim();
  const trimmedDescription = description.trim();
  useEffect(() => {
    draftListener.current?.({ spec: runnable, name: trimmedName, description: trimmedDescription });
  }, [runnable, trimmedName, trimmedDescription]);

  return (
    // `rk-builder-solo` collapses the two-column grid to one. WITHOUT IT the
    // preview would simply take the first column's place and be laid out in the
    // 280–340px track the configuration was sized for — a full-width table
    // rendered a third of the way across the card.
    <div className={showConfig ? 'rk-builder' : 'rk-builder rk-builder-solo'}>
      {/* THE CONFIGURATION COLUMN, and only for a reader who may change the
          report's shape. See `configurable`: for a recipient holding
          `report.view` alone it is not drawn at all, and the builder is one
          column of filter bar and result. Collapsing it is the same absence
          asked for by hand, for the width. */}
      {showConfig && (
        <div className="rk-builder-config">
          {models.length > 1 && (
            <section className="rk-step rk-step-model">
              <ModelPicker models={models} value={model.id} onChange={onModel} />
            </section>
          )}

          {/* ALWAYS drawn, even where the model offers one kind. A model may
              declare what it can be built as — Utilization is a pivot, because a
              listing of a ratio to a capacity nobody logs has no row to be one
              of — and the first cut of that hid the control entirely on the
              reasoning that a button with no alternative answers a question
              nobody asked. That was wrong in the place it mattered: the reader
              lost the only thing on screen that NAMED what they were building,
              and a step that silently disappears reads as a broken screen rather
              than as a settled choice. So the offered kinds render whatever
              their number, and the one in force is the pressed one. Clicking it
              is safe — `setKind` returns the spec untouched when the kind has
              not changed, so the lone button cannot clear the shelves. */}
          <section className="rk-step rk-step-kind">
            <div className="rk-picks">
              {/* Tabular first where the model offers it, and the one a fresh
                  report starts on: listing the rows is the commoner errand, and
                  it is the shape somebody arrives already knowing they want. The
                  SPEC still defaults to pivot — that default exists so a spec
                  saved before `kind` existed parses as what it was, which is a
                  different question. */}
              {kinds.map((kind) => (
                <button
                  type="button"
                  key={kind}
                  className="rk-pick"
                  aria-pressed={spec.kind === kind}
                  // Where this is the only kind, it is pressed and cannot become
                  // anything else — `setKind` returns the spec untouched for the
                  // kind already in force, so a press is silent. Without this a
                  // reader is told "toggle button, pressed", activates it to
                  // switch, and gets no answer at all. `aria-disabled` rather
                  // than `disabled`: the button keeps its pressed styling and
                  // stays focusable, so it still reads as the label it is here
                  // to be. Absent, not `false`, where there IS a choice.
                  aria-disabled={kinds.length === 1 || undefined}
                  title={KIND_HINTS[kind]}
                  onClick={() => dispatch({ type: 'setKind', kind })}
                >
                  {REPORT_KIND_LABELS[kind]}
                </button>
              ))}
            </div>
            <p className="rk-hint">{KIND_HINTS[spec.kind]}</p>
          </section>

          {savedAsKind && (
            <p className="rk-kind-notice">
              Saved as a {REPORT_KIND_LABELS[savedAsKind]} report, which {model.label} no longer
              offers. Opened as {REPORT_KIND_LABELS[spec.kind]}, so what it was saved with is not
              carried over.
            </p>
          )}

          {detail ? (
            <>
              {/* 1 — Columns */}
              <section className="rk-step">
                {/* One shelf, so its Add rides on the heading rather than on a
                    line of its own beneath it. */}
                <div className="rk-step-head">
                  <h3>
                    <span className="rk-stepno">1</span> Columns
                  </h3>
                  <Picker
                    trigger="add"
                    title="Choose columns"
                    searchPlaceholder="Search columns…"
                    emptyText="This report has no fields to list."
                    options={columnOptions()}
                    multiple
                    value={spec.columns}
                    // Reconciled, not replaced: ticking a twenty-third column
                    // must not reshuffle the twenty-two the reader dragged into
                    // order.
                    onChange={(ids) =>
                      dispatch({ type: 'setColumns', columns: reconcile(spec.columns, ids) })
                    }
                    bulkActions="select-all"
                    popoverWidth="auto"
                  />
                </div>
                <Shelf
                  axis="columns"
                  rows={spec.columns.map((id) => {
                    // A listing's columns are dimensions AND measures on one
                    // shelf, so the format control is per ROW rather than per
                    // shelf: a name has nothing to round, a number does. The
                    // pivot reaches the same menu from its Values shelf — one
                    // entrance per kind, so formatting is not a thing only
                    // discoverable by right-clicking a table.
                    const measure = findMeasure(model, id);
                    return {
                      id,
                      label: findDimension(model, id)?.label ?? measure?.label ?? id,
                      formula: measure?.formulaText,
                      settings: measure && (
                        <FormatBadges
                          measure={measure}
                          spec={spec}
                          open={formatTarget?.measureId === id}
                          onOpen={openFormat}
                          dispatch={dispatch}
                        />
                      ),
                    };
                  })}
                  onReorder={(from, to) => {
                    const next = [...spec.columns];
                    const [moved] = next.splice(from, 1);
                    if (moved) next.splice(to, 0, moved);
                    dispatch({ type: 'setColumns', columns: next });
                  }}
                  onRemove={(fieldId) => dispatch({ type: 'removeColumn', fieldId })}
                  onDrop={(payload, index) => {
                    const from = spec.columns.indexOf(payload.dimensionId);
                    if (from === -1) return;
                    const next = [...spec.columns];
                    const [moved] = next.splice(from, 1);
                    if (!moved) return;
                    const to = index === undefined ? next.length : index > from ? index - 1 : index;
                    next.splice(to, 0, moved);
                    dispatch({ type: 'setColumns', columns: next });
                  }}
                  drag={drag}
                  setDrag={setDrag}
                  dropIndex={dropIndex}
                  setDropIndex={setDropIndex}
                  activeZone={activeZone}
                  setActiveZone={setActiveZone}
                />
              </section>

              {/* 2 — Sort */}
              <section className="rk-step">
                <div className="rk-step-head">
                  <h3>
                    <span className="rk-stepno">2</span> Sort
                  </h3>
                  <Picker
                    trigger="add"
                    title="Sort rows by"
                    searchPlaceholder="Search…"
                    emptyText="Pick a column first — a report sorts by what it shows."
                    options={columnSortOptions()}
                    multiple
                    value={sortValue('rows')}
                    onChange={(ids) => applySort('rows', ids)}
                    bulkActions="select-all"
                    popoverWidth="auto"
                  />
                </div>
                <Shelf
                  axis="sort-rows"
                  rows={sortRows('rows')}
                  onReorder={(from, to) => {
                    const next = [...spec.rowSort];
                    const [moved] = next.splice(from, 1);
                    if (moved) next.splice(to, 0, moved);
                    setChain('rows', next);
                  }}
                  onRemove={(id) =>
                    setChain(
                      'rows',
                      spec.rowSort.filter((s) => sortKey(s) !== id),
                    )
                  }
                  onDrop={(payload, index) => {
                    const from = spec.rowSort.findIndex((s) => sortKey(s) === payload.dimensionId);
                    if (from === -1) return;
                    const next = [...spec.rowSort];
                    const [moved] = next.splice(from, 1);
                    if (!moved) return;
                    const to = index === undefined ? next.length : index > from ? index - 1 : index;
                    next.splice(to, 0, moved);
                    setChain('rows', next);
                  }}
                  drag={drag}
                  setDrag={setDrag}
                  dropIndex={dropIndex}
                  setDropIndex={setDropIndex}
                  activeZone={activeZone}
                  setActiveZone={setActiveZone}
                />
              </section>

              {/* 3 — Display */}
              <section className="rk-step">
                <h3>
                  <span className="rk-stepno">3</span> Display
                </h3>
                <div className="rk-toggles">
                  <button
                    type="button"
                    className="rk-toggle"
                    aria-pressed={spec.totals.col}
                    // ONE total, not the pivot's three: a listing has no column
                    // axis to total across and no grand corner where two meet.
                    title="A total row beneath the listing, for each column that can be added"
                    onClick={() => dispatch({ type: 'toggleTotals' })}
                  >
                    Total
                  </button>
                </div>
              </section>
            </>
          ) : (
            <>
              {/* 1 — Rows, 2 — Columns.
                  ONE loop, because the two shelves differ in three details and
                  agree on everything else — and drawn as two sections rather
                  than two shelves under a "Group by" heading, so a field's own
                  order can live on the field. That is what closed the old Sort
                  step: an axis-wide chain two sections away from the shelf it
                  ordered was the only thing "Row order" could have been called,
                  because the shelf it belonged to was already called Rows. */}
              {(['rows', 'cols'] as const).map((axis, step) => (
                <section className="rk-step" key={axis}>
                  <div className="rk-step-head">
                    <h3>
                      <span className="rk-stepno">{step + 1}</span>{' '}
                      {axis === 'rows' ? 'Rows' : 'Columns'}
                    </h3>
                    <Picker
                      trigger="add"
                      title={`Group ${axis === 'rows' ? 'rows' : 'columns'} by`}
                      searchPlaceholder="Search dimensions…"
                      emptyText="This report has no dimensions."
                      options={dimensionOptions(axis)}
                      multiple
                      value={axis === 'rows' ? spec.rows : spec.cols}
                      // One action per id rather than a single `set`, so the
                      // reducer applies the group-by ceiling to each: past four,
                      // the rest come back refused with the reason on the row
                      // instead of being added and silently dropped.
                      onChange={(ids) => {
                        const current = axis === 'rows' ? spec.rows : spec.cols;
                        const chosen = new Set(ids);
                        for (const dimensionId of current) {
                          if (!chosen.has(dimensionId)) {
                            dispatch({ type: 'removeDimension', dimensionId });
                          }
                        }
                        for (const dimensionId of ids) {
                          if (!current.includes(dimensionId)) {
                            dispatch({ type: 'addDimension', dimensionId, axis });
                          }
                        }
                      }}
                      bulkActions="select-all"
                      popoverWidth="auto"
                    />
                  </div>
                  <Shelf
                    axis={axis}
                    hint={
                      axis === 'rows'
                        ? 'What each group of rows is one of — outermost first.'
                        : 'Optional. Breaks every measure out across these, left to right.'
                    }
                    rows={(axis === 'rows' ? spec.rows : spec.cols).map((id, index) => ({
                      id,
                      label: label(id),
                      settings: fieldSettings(axis, id, index),
                    }))}
                    onReorder={(from, to) => dispatch({ type: 'reorder', axis, from, to })}
                    onRemove={(dimensionId) => dispatch({ type: 'removeDimension', dimensionId })}
                    onDrop={(payload, index) =>
                      dispatch({
                        type: 'moveDimension',
                        dimensionId: payload.dimensionId,
                        axis,
                        index,
                      })
                    }
                    drag={drag}
                    setDrag={setDrag}
                    dropIndex={dropIndex}
                    setDropIndex={setDropIndex}
                    activeZone={activeZone}
                    setActiveZone={setActiveZone}
                  />
                </section>
              ))}

              {/* 3 — Values.
                  LAST, where Rows and Columns used to send it. The sentence a
                  pivot is runs that way round — these rows, broken out by these
                  columns, showing these numbers — and it is the order the same
                  three shelves are asked in everywhere else. */}
              <section className="rk-step">
                <div className="rk-step-head">
                  <h3>
                    <span className="rk-stepno">3</span> Values
                  </h3>
                  <Picker
                    trigger="add"
                    title="Choose measures"
                    searchPlaceholder="Search measures…"
                    emptyText="This report has no measures."
                    options={measureOptions}
                    multiple
                    value={spec.measures}
                    onChange={(ids) =>
                      dispatch({ type: 'setMeasures', measures: reconcile(spec.measures, ids) })
                    }
                    bulkActions="select-all"
                    popoverWidth="auto"
                  />
                </div>
                <Shelf
                  axis="measures"
                  hint="The numbers this report is of."
                  rows={spec.measures.flatMap((measureId) => {
                    const measure = findMeasure(model, measureId);
                    if (!measure) return [];
                    return [
                      {
                        id: measureId,
                        label: measure.label,
                        // The formula rides with the measure — behind an icon, so
                        // "where did this number come from?" is one hover away
                        // without a paragraph of SQL-ish text under every name.
                        formula: measure.formulaText,
                        // `Show as` stays beside the label because it changes
                        // what the number MEANS — a cell under it is a share,
                        // not the measure. Formatting changes only how it
                        // reads, so it lives on the strip below.
                        controls: (
                          <label className="rk-rowselect">
                            <span className="rk-sr">Show {measure.label} as</span>
                            <select
                              aria-label={`Show ${measure.label} as`}
                              value={showAsFor(spec, measureId)}
                              onChange={(e) =>
                                dispatch({
                                  type: 'setShowAs',
                                  measureId,
                                  mode: e.target.value as ShowAsMode,
                                })
                              }
                            >
                              {SHOW_AS_MODES.map((mode) => {
                                const verdict = canShowAs(measure, mode, spec);
                                return (
                                  <option key={mode} value={mode} disabled={!verdict.ok}>
                                    {SHOW_AS_LABELS[mode]}
                                  </option>
                                );
                              })}
                            </select>
                          </label>
                        ),
                        settings: (
                          <FormatBadges
                            measure={measure}
                            spec={spec}
                            open={formatTarget?.measureId === measureId}
                            onOpen={openFormat}
                            dispatch={dispatch}
                          />
                        ),
                      },
                    ];
                  })}
                  onReorder={(from, to) => {
                    const next = [...spec.measures];
                    const [moved] = next.splice(from, 1);
                    if (moved) next.splice(to, 0, moved);
                    dispatch({ type: 'setMeasures', measures: next });
                  }}
                  onRemove={(measureId) => dispatch({ type: 'toggleMeasure', measureId })}
                  onDrop={(payload, index) => {
                    const from = spec.measures.indexOf(payload.dimensionId);
                    if (from === -1) return;
                    const next = [...spec.measures];
                    const [moved] = next.splice(from, 1);
                    if (!moved) return;
                    const to = index === undefined ? next.length : index > from ? index - 1 : index;
                    next.splice(to, 0, moved);
                    dispatch({ type: 'setMeasures', measures: next });
                  }}
                  drag={drag}
                  setDrag={setDrag}
                  dropIndex={dropIndex}
                  setDropIndex={setDropIndex}
                  activeZone={activeZone}
                  setActiveZone={setActiveZone}
                />
              </section>

              {/* 4 — Display */}
              <section className="rk-step">
                <h3>
                  <span className="rk-stepno">4</span> Display
                </h3>
                {/* ONLY THE SHAPES THIS REPORT CAN ACTUALLY BE DRAWN AS. A
                    refused chart is DROPPED, not drawn struck through: the
                    struck-out row read as five broken buttons, and every one of
                    them carried its refusal in a tooltip nobody hovers — an
                    answer to a question the reader was not asking, since they
                    came here to choose a shape rather than to diagnose one.
                    Removing them makes the row say the one true thing instead:
                    here is what this report can be.

                    Nothing is lost by the removal. The refusals are all about
                    the SHELVES — a pie wants one row dimension and no series, a
                    line wants time on Rows — so the option grows back the moment
                    the shape it needs exists, because availability is recomputed
                    from the spec on every render.

                    The row can never empty and the pressed option is always in
                    it: `canVisualize` returns `ok` for `table` unconditionally,
                    and `settle` coerces a spec whose viz stopped being drawable
                    back to `table`. */}
                <div className="rk-picks">
                  {VIZ_KINDS.filter((viz) => vizOptions.get(viz)?.ok !== false).map((viz) => (
                    <button
                      type="button"
                      key={viz}
                      className="rk-pick"
                      aria-pressed={spec.viz === viz}
                      onClick={() => dispatch({ type: 'setViz', viz })}
                    >
                      {VIZ_LABELS[viz]}
                    </button>
                  ))}
                </div>

                {savedAsViz && (
                  <p className="rk-kind-notice">
                    Saved as a {VIZ_LABELS[savedAsViz.viz].toLowerCase()}, which this report’s shape
                    no longer supports. {savedAsViz.reason} Opened as a table, and saving keeps it
                    that way.
                  </p>
                )}

                <div className="rk-toggles">
                  <button
                    type="button"
                    className="rk-toggle"
                    aria-pressed={spec.totals.row && spec.totals.col}
                    // "Grand total", not "Totals". There is a per-level Subtotal
                    // button two sections up, and two controls named the same
                    // thing doing different things is a coin toss — the reason
                    // this one could not be found was that it read as the one
                    // already on the Rows shelf.
                    title="A total row, a total column, and the grand total where they meet"
                    onClick={() => dispatch({ type: 'toggleTotals' })}
                  >
                    Grand total
                  </button>
                </div>
              </section>
            </>
          )}
        </div>
      )}

      {/* Preview */}
      <div className="rk-builder-preview">
        {/* ONE BAND ACROSS THE TOP of the result, carrying the toggle and the
            filters. The toggle rides HERE rather than in the column it opens,
            for the obvious reason: a control that lives inside the panel goes
            away with it and there is nothing left to press. It leads the band
            because it is the leftmost thing on the row, sitting directly over
            the edge the panel appears along.

            NOT a scroll container, and it must not become one: an open filter
            chip paints its panel outside the bar, so anything clipping here
            clips the panel — the trap `.rk-builder` documents. */}
        <div className="rk-preview-bar">
          {/* THE COLLAPSE. Mounted in this band — first in tab order, level
              with the chips — but DRAWN on the divider to its left, the line it
              moves (see `.rk-config-toggle`). One element whichever way the
              panel is, so a press never unmounts the focused button. Nothing
              at all when the reader may not configure the report: there is no
              column either way. */}
          {configurable && (
            <ConfigToggle open={showConfig} onToggle={() => setConfigOpen((open) => !open)} />
          )}

          {/* The filter bar, ALWAYS — the period is a chip on it, and every
              report has a period even on a model that offers nothing else to
              filter by. It is also the ONLY thing a reader without
              `configurable` can change, which is the point of keeping it out
              of the column that goes away. */}
          <FilterBar
            // DEFINITIONS, LOADING, SAVING: this model's fields, the spec's
            // filters, and the spec reducer — which applies the bar's actions
            // with the same `applyFilterAction` a dashboard does.
            fields={filterFields}
            filters={spec.filters}
            onAction={dispatch}
            // The period the chips sit beside, macro and window both. A hosted
            // control fetches its members for THAT window, so the two cannot
            // describe different spans of time.
            period={{
              value: specDateRange(spec),
              window: { from: spec.from, to: spec.to },
              onChange: (range) => dispatch({ type: 'setPeriod', range }),
              presets: periodPresets,
              // The reducer's own refusal, said by the chip: it keeps the panel
              // open on the reason instead of closing over an untaken change.
              problem: refusePeriod,
            }}
            options={filterOptions}
            renderControl={renderFilterControl}
          />
        </div>

        {savedOverPeriod && (
          <p className="rk-kind-notice">
            Saved over {savedOverPeriod}, which {model.label} doesn’t offer. Opened over{' '}
            {DATE_RANGE_PRESET_LABELS[spec.period ?? 'custom'].toLowerCase()} instead, and saving
            keeps it.
          </p>
        )}

        {/* HIDDEN when the host draws it. See `showTitle` — the app that
            embeds this builder has a page header carrying the same name and
            the same description, and printing them again here made the two
            lines the reader is looking at a copy of the two above them. */}
        {showTitle && (
          <header className="rk-preview-head">
            <ReportTitle
              name={name}
              description={description}
              derived={derived}
              editable={editable}
              onCommitName={commitName}
              onCommitDescription={commitDescription}
            />
          </header>
        )}

        {loading && <p className="rk-empty">Running…</p>}
        {error && <p className="rk-error">{error.message}</p>}
        {!runnable && !loading && (
          <p className="rk-empty">
            {detail
              ? 'The preview appears once a column is chosen.'
              : 'The preview appears once a measure and a row dimension are chosen.'}
          </p>
        )}
        {runnable && data && !loading && (
          <>
            <ReportView
              model={model}
              spec={runnable}
              result={data}
              plan={plan ?? undefined}
              onSort={setSortBy}
              // Not gated on `editable`, as sorting is not: folding is how a
              // reader reads a report, and a shared reader reads it too.
              onToggleGroup={(key) => dispatch({ type: 'toggleGroup', key })}
              onCollapseLevel={(dimensionId, collapsed) =>
                dispatch({ type: 'setLevelCollapsed', dimensionId, collapsed })
              }
              // Not gated: drilling is reading, and a recipient reads too.
              onDrill={onDrill}
            />
            <ReportFooter model={model} spec={runnable} result={data} plan={plan ?? undefined} />
          </>
        )}
        {/* OUTSIDE the block above, so it survives the fetch it starts. Inside,
            every page turn unmounted the control the reader had just clicked
            and replaced the whole preview with "Running…" — and `busy` could
            never be true, which is the state the buttons stand down in. */}
        {runnable && detail && (data || loading) && (
          <Pager
            offset={offset}
            pageSize={pageSize}
            shown={data?.rows.length ?? 0}
            total={data?.meta.totalRows ?? rememberedTotal}
            busy={loading}
            pastEnd={data?.meta.pastEnd}
            onPage={(next) => dispatch({ type: 'setPage', offset: next })}
            onPageSize={(size) => dispatch({ type: 'setPageSize', size })}
          />
        )}
        {/* THE STATEMENT, WHEN THE HOST ASKED FOR IT — there is no toggle here
            any more, and that is the point. The SQL is a disclosure the server
            returns only to a caller who both holds the grant for it and asked;
            whether it is on this page is the host's decision, made once, and
            the presence of `meta.sql` IS that decision arriving. A control the
            reader can press is the wrong shape for it: on most sessions there
            would be nothing behind the press, which is how the old "Show plan"
            button came to be a button that showed no SQL.
            The plan rides along, because the statement alone is half the
            answer — a sort or a total applied after the rows arrived appears
            nowhere in it. */}
        {data?.meta.sql && (
          <section className="rk-disclosure">
            <h4>The query that ran</h4>
            <pre className="rk-sql">{data.meta.sql}</pre>
            {plan && <PlanNote plan={plan} />}
          </section>
        )}
        {drillTray}

        {/* NO ACTION FOOTER. Saving, sharing and placing on a dashboard are the
            host's, and they belong wherever that page puts the rest of its
            actions — which for this app is the top of the page, beside Share
            and Delete. A library button here would have split one object's
            actions across two places and fixed the position of half of them.
            `onDraftChange` is how the host gets what it needs to render them. */}
      </div>

      {/* At the builder's ROOT, not inside the column that opened it: the
          config column is a sticky stacking context and the preview is
          another, and a menu belonging to either paints under the other. */}
      {formatTarget && formatMeasure && (
        <FormatMenu
          key={formatTarget.measureId}
          ctx={formatContext(formatMeasure, spec)}
          // Whether anything is STORED, not whether anything is visible. A key
          // that no longer applies — a symbol on a measure whose format changed
          // — draws nothing, and keying Reset off what is drawn would leave it
          // in the spec with nothing in the UI able to clear it.
          dirty={Object.keys(valueFormatFor(spec, formatMeasure.id)).length > 0}
          anchor={formatTarget.anchor}
          toggleRef={formatToggleRef}
          values={data?.rows.map((row) => row.values[formatMeasure.id])}
          onChange={(key, value) =>
            dispatch({ type: 'setValueFormat', measureId: formatMeasure.id, key, value })
          }
          onReset={() => dispatch({ type: 'resetValueFormat', measureId: formatMeasure.id })}
          onClose={closeFormat}
        />
      )}
    </div>
  );
}

/**
 * The control that puts the configuration column away, and brings it back.
 *
 * ON THE DIVIDER, as a round tab straddling the line between the panel and
 * the result — the edge that moves when it is pressed. Two earlier homes did
 * not read: a strip atop the column cost the panel a whole row and had to jump
 * elsewhere when the column went away, and a `PanelLeft` icon at the head of
 * the filter band looked like one of the filters. A handle on the edge is the
 * convention for a collapsible side panel, and a chevron says which way it
 * goes.
 *
 * AN ICON, not a word. The word this started as — "Configure", beside FILTERS
 * — was unguessable: nothing about a row of filter chips suggests anything
 * about the shelves that shape the report.
 *
 * The `aria-label` changes with the state and the icon does too, because there
 * is no text to reflow — the objection to a label that moves does not apply to
 * a fixed-size square. `aria-expanded` is still here: it is the property a
 * screen reader announces, and it says the same thing the icon does.
 */
function ConfigToggle({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  const label = open ? 'Hide the report’s setup' : 'Show the report’s setup';
  const Icon = open ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      className="rk-config-toggle"
      aria-expanded={open}
      aria-label={label}
      title={label}
      onClick={onToggle}
    >
      <Icon className="rk-config-toggle-icon" aria-hidden />
    </button>
  );
}

/**
 * The report's TITLE BLOCK — its name, what it is, and what its author says
 * about it. Two of the three are edited where they are read.
 *
 * IN PLACE, not in a dialog behind a Rename item: naming is how a draft becomes
 * a report somebody else can find, and describing it is how a recipient decides
 * whether to run it (CR-3.7). Both belong at the moment the reader is looking
 * at what they built, not two clicks away from it. Click either line and it
 * becomes the field it already looked like.
 *
 * TWO LINES, in this order and for this reason:
 *
 *  - the NAME, or — while it has none — {@link describeSpec}'s sentence
 *    standing in for it, "Utilized Hours by Employee across Month". Once the
 *    report HAS a name, that sentence is gone: it restated a draft the author
 *    has already read off the panel beside it, and under a name somebody chose
 *    it read as a machine correcting them;
 *  - the DESCRIPTION, which is the author's own sentence and is never written
 *    for them. An empty one shows a prompt rather than nothing, because a line
 *    that is not there is a line nobody knows they can fill in.
 */
function ReportTitle({
  name,
  description,
  derived,
  editable,
  onCommitName,
  onCommitDescription,
}: {
  name: string;
  description: string;
  derived: string;
  editable: boolean;
  onCommitName: (name: string) => void;
  onCommitDescription: (description: string) => void;
}) {
  return (
    <div className="rk-titlebox">
      {/* STILL A HEADING. Making the name editable must not cost the preview
          its place in the document outline — a screen reader navigating by
          heading is how someone finds the report on this page, and a bare
          button is not on that list. */}
      <InlineEdit
        heading
        editable={editable}
        value={name}
        placeholder={derived}
        // Softened when it is standing in for a name, which is what says it is
        // a description rather than the name somebody gave this report.
        emptyClassName="rk-title-unnamed"
        label="Report name"
        hint="Click to rename this report"
        maxLength={MAX_REPORT_NAME}
        className="rk-title"
        onCommit={onCommitName}
      />
      {/* The PROMPT is an invitation, so it appears only where it can be taken
          up. A reader who cannot save has nothing to gain from being asked for
          a description, and the empty line would be an offer this page cannot
          honour — the description a report HAS is still shown to them. */}
      {editable || description ? (
        <InlineEdit
          editable={editable}
          value={description}
          // NOT a suggestion. A description prefilled with a machine sentence
          // would be saved unread by nearly everyone — leaving every report
          // "described" and none of them described. The prompt asks; it does
          // not answer.
          placeholder="Add a description"
          emptyClassName="rk-title-desc-empty"
          label="Report description"
          hint="Click to describe this report"
          maxLength={MAX_REPORT_DESCRIPTION}
          multiline
          className="rk-title-desc"
          onCommit={onCommitDescription}
        />
      ) : null}
    </div>
  );
}

/**
 * One line of the title block, edited in place: a button that turns into the
 * field it already looked like.
 *
 * ONE COMPONENT for the name and the description because it is one gesture —
 * click, type, Enter or blur to commit, Escape to abandon — and the abandon
 * handling below is subtle enough that a second copy of it would be a second
 * copy of the bug it fixes.
 *
 * The button and the field are sized IDENTICALLY (`rk-inline`, plus whichever
 * line-specific class the caller passes) so the text does not jump when it
 * turns into a field. That is the whole illusion: the thing you clicked is the
 * thing you are now typing in, not a control that replaced it.
 *
 * Committed on Enter and on blur, never per keystroke: the host persists what
 * it is handed, and a rename per character is a rename per character.
 */
function InlineEdit({
  value,
  placeholder,
  emptyClassName,
  label,
  hint,
  maxLength,
  className,
  heading,
  multiline,
  editable,
  onCommit,
}: {
  value: string;
  /** Stands in for an empty value, and is the field's placeholder while typing. */
  placeholder: string;
  /** Applied to the stand-in, so it does not read as a value somebody set. */
  emptyClassName: string;
  /** The accessible name of both the button and the field it opens. */
  label: string;
  /** The button's tooltip — how a reader learns the line is a control at all. */
  hint: string;
  maxLength: number;
  /** The line's own class: what it looks like, in both modes. */
  className: string;
  /** Wrap the read view in an `h3`. The name is in the outline; a description is not. */
  heading?: boolean;
  /** A textarea rather than an input: a description is a sentence, not a word. */
  multiline?: boolean;
  /** False renders the line as text. See {@link ReportBuilderProps.editable}. */
  editable: boolean;
  onCommit: (next: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  // Resync when the value changes underneath — a save that normalises it, a
  // host resetting the builder. Same mirror the period field uses, and for the
  // same reason: this is a view of the value, not a second copy of it.
  const [committed, setCommitted] = useState(value);
  if (committed !== value) {
    setCommitted(value);
    setDraft(value);
  }

  // Escape has to survive the blur that follows it. Removing the field does not
  // dispatch a blur in React, but it can also lose focus to a click landing
  // elsewhere in the same tick — and `commit` closes over the `draft` of the
  // render it was made in, which is the pre-Escape one. A ref is read at call
  // time, so the abandon wins however the blur arrives.
  //
  // CLEARED WHEN THE FIELD OPENS, not only when a commit consumes it. Whether
  // Escape is followed by a blur is a browser detail: removing a focused
  // element dispatches no blur in most engines, so `commit` never ran, the flag
  // stayed set, and it then swallowed the NEXT edit's commit — one abandoned
  // typo silently discarded the rename typed after it.
  const abandoned = useRef(false);
  const startEditing = () => {
    abandoned.current = false;
    setDraft(value);
    setEditing(true);
  };

  const commit = () => {
    setEditing(false);
    if (abandoned.current) {
      abandoned.current = false;
      return;
    }
    // Trimmed, and an empty value means UNSET rather than a value that is "".
    // That is also the way back to the stand-in, so a name or a description can
    // be undone with the same control that set it.
    onCommit(draft.trim());
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    // Enter commits in BOTH, the textarea included. A description is a sentence
    // and Enter is what somebody presses when they have finished typing one;
    // Shift+Enter is there for the rare description that wants a line break.
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      e.currentTarget.blur();
    }
    if (e.key === 'Escape') {
      abandoned.current = true;
      setDraft(value);
      setEditing(false);
    }
  };

  if (!editable || !editing) {
    // TITLE = THE VALUE, once there is one. Both lines truncate in read mode —
    // the name to one line with an ellipsis, the description clamped to two —
    // and the full text was reachable only by opening the field, which is not
    // an option for a reader who may not edit and is a hazard for one who may.
    // The hint takes over when the line is empty, which is exactly when there
    // is no text to reveal and the affordance is the thing worth explaining.
    const title = value || hint;
    const text = <span className={value ? undefined : emptyClassName}>{value || placeholder}</span>;
    const read = editable ? (
      <button
        type="button"
        className={`rk-inline ${className}`}
        title={title}
        onClick={startEditing}
      >
        {text}
        {/* The pencil only on approach. Permanently visible it becomes
            furniture; never visible and the line is a control nobody knows they
            can press. */}
        <span className="rk-title-pencil" aria-hidden="true">
          ✎
        </span>
      </button>
    ) : (
      // A SPAN, not a disabled button: there is nothing here to press, and a
      // control that announces itself as unavailable invites the reader to go
      // looking for the permission that would enable it. `rk-inline` keeps the
      // box identical either way, so the line does not shift between readers.
      <span className={`rk-inline ${className}`} title={title}>
        {text}
      </span>
    );
    // The read view is INSIDE the h3 rather than replacing it, so the heading's
    // accessible name is the report's.
    return heading ? <h3>{read}</h3> : read;
  }

  const shared = {
    'aria-label': label,
    value: draft,
    maxLength,
    placeholder,
    className: `rk-inline rk-inline-field ${className}`,
    onChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setDraft(e.target.value);
      grow(e.target);
    },
    onBlur: commit,
    onKeyDown,
  };

  // `autoFocus` on the elements rather than in `shared`: the click that opened
  // this field WAS the request to type in it, so stealing focus is what the
  // reader just asked for — and the suppression has to sit on the attribute it
  // is suppressing.
  return multiline ? (
    // biome-ignore lint/a11y/noAutofocus: the field only exists because it was just opened to be typed in
    <textarea autoFocus ref={grow} rows={2} {...shared} />
  ) : (
    // biome-ignore lint/a11y/noAutofocus: the field only exists because it was just opened to be typed in
    <input autoFocus {...shared} />
  );
}

/**
 * Size a textarea to what is in it.
 *
 * A description runs to five hundred characters and the read view is two lines
 * tall — so a field fixed at two rows would open onto a porthole, and editing
 * the middle of a paragraph would mean scrolling a box smaller than the
 * sentence being scrolled. It grows on mount (as a ref callback, which is where
 * the element first exists) and on every keystroke.
 *
 * `height = ''` FIRST, so `scrollHeight` is measured against the CSS height
 * rather than against whatever this function set last time — otherwise the box
 * only ever grows, and deleting a paragraph leaves the hole it was in.
 *
 * PLUS THE BORDER. `scrollHeight` is content + padding and stops there, but the
 * field is `border-box`, so a height set straight from it has to find room for
 * the 1px rules above and below inside the same number — and the last line
 * loses two pixels off the bottom, which `overflow: hidden` clips rather than
 * scrolls. `offsetHeight - clientHeight` is that difference, measured rather
 * than assumed, so a host that restyles the border does not reopen this.
 */
function grow(el: HTMLTextAreaElement | HTMLInputElement | null): void {
  if (!(el instanceof HTMLTextAreaElement)) return;
  el.style.height = '';
  el.style.height = `${el.scrollHeight + el.offsetHeight - el.clientHeight}px`;
}

/**
 * What the query did, and what the browser did afterwards.
 *
 * Shown beside the SQL because the SQL alone is only half the answer — a sort
 * or a share applied after the rows arrived appears nowhere in it.
 */
function PlanNote({ plan }: { plan: QueryPlan }) {
  return (
    <dl className="rk-plan">
      {plan.rationale.map((r) => (
        <div key={`${r.step}-${r.stage}-${r.because}`}>
          <dt>
            {r.step} · {r.stage === 'sql' ? 'in the query' : 'in the browser'}
          </dt>
          <dd>{r.because}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * One measure's format: its settings as removable badges, and the popup.
 *
 * A badge per SET attribute, labelled by the registry — and for the negative
 * style, labelled with the style RENDERED, so the shelf says what the column
 * will look like without anything being opened. Each badge's ✕ clears that one
 * attribute and leaves the rest, which is the thing a badge does that a panel
 * cannot.
 */
function FormatBadges({
  measure,
  spec,
  open,
  onOpen,
  dispatch,
}: {
  measure: Measure;
  spec: UIReportSpec;
  open: boolean;
  onOpen: OnFormatColumn;
  dispatch: (action: SpecAction) => void;
}) {
  const ctx = formatContext(measure, spec);
  const chosen = valueFormatFor(spec, measure.id);
  const keys = activeFormatKeys(chosen, ctx);

  return (
    <span className="rk-fmtbadges">
      {/* FIRST, so it never moves: badges appear and go as the reader
          formats, and a trigger after them slid sideways under the open
          menu anchored to it. */}
      <button
        type="button"
        className="rk-fmttrigger"
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={`Format ${measure.label}`}
        onClick={(event) => {
          const el = event.currentTarget;
          onOpen(measure.id, () => (el.isConnected ? el.getBoundingClientRect() : null), el);
        }}
      >
        Format
      </button>
      {keys.map((key) =>
        key === 'rules' ? (
          // ONE PILL PER RULE, painted in the rule's own colour — the colour is
          // what the table shows, and a single "2 rules" pill could not show
          // two. Each ✕ removes that rule and leaves the others in order.
          ctx.resolved.rules.map((rule, index) => (
            <span
              // biome-ignore lint/suspicious/noArrayIndexKey: a rule's position IS its identity (its precedence)
              key={`rule-${index}`}
              className={`rk-fmtbadge rk-tone rk-tone-${rule.tone}`}
            >
              <span className="rk-fmtbadge-text">{ruleSample(rule, ctx.resolved)}</span>
              <button
                type="button"
                className="rk-icon"
                aria-label={`Clear highlight rule ${index + 1} for ${measure.label}`}
                title="Clear this highlight"
                onClick={() => {
                  const next = ctx.resolved.rules.filter((_, i) => i !== index);
                  dispatch({
                    type: 'setValueFormat',
                    measureId: measure.id,
                    key: 'rules',
                    value: next.length === 0 ? undefined : next,
                  });
                }}
              >
                ✕
              </button>
            </span>
          ))
        ) : (
          <span key={key} className="rk-fmtbadge">
            <span
              className={
                badgeIsNegative(key, chosen) ? 'rk-fmtbadge-text rk-neg' : 'rk-fmtbadge-text'
              }
            >
              {badgeFor(key, chosen, ctx)}
            </span>
            <button
              type="button"
              className="rk-icon"
              // Name the ATTRIBUTE, not the value: four pills all reading "✕"
              // are four controls a screen reader cannot tell apart.
              aria-label={`Clear ${FORMAT_OPTIONS[key].label} for ${measure.label}`}
              title={`Clear ${FORMAT_OPTIONS[key].label}`}
              onClick={() =>
                dispatch({ type: 'setValueFormat', measureId: measure.id, key, value: undefined })
              }
            >
              ✕
            </button>
          </span>
        ),
      )}
    </span>
  );
}
